import { addDays, daysBetween, todayIso, type BudgetPeriod, type Category, type Report, type ReportPace } from '@fin/shared';
import { scaleBand, scaleLinear } from 'd3-scale';
import { arc, pie, type PieArcDatum } from 'd3-shape';
import { useState } from 'react';
import { ErrorBox, Icon, Input, PageHead, Section, Select, Segmented, Tile, cx } from '../../components/ui.tsx';
import { qs } from '../../lib/api.ts';
import { useCategories, useWallets } from '../../lib/data.ts';
import { dateShort, monthShort, pct, rub } from '../../lib/format.ts';
import { categoryColor, categoryIcon } from '../../lib/icons.tsx';
import { useQuery } from '../../lib/query.ts';
import { Link, setQuery, useLocation } from '../../lib/router.tsx';
import { TrendChart, ReportSkeleton } from './charts.tsx';
import { groupCategories } from './model.ts';

function monthStart(iso: string, back = 0) {
  const [y, m] = iso.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 - back, 1));
  return d.toISOString().slice(0, 10);
}

export function ReportsPage() {
  const today = todayIso();
  const { query } = useLocation();
  const current = useQuery<BudgetPeriod>('/periods/current');
  const presets: Record<string, [string, string]> = {
    month: [monthStart(today), today],
    prev: [monthStart(today, 1), addDays(monthStart(today), -1)],
    quarter: [monthStart(today, 2), today],
    year: [monthStart(today, 11), today],
  };
  if (current.data) presets.period = [current.data.startDate, current.data.endDate];
  const from = query.get('from') ?? presets.month[0];
  const to = query.get('to') ?? presets.month[1];
  const walletId = query.get('wallet') ?? '';
  const preset = Object.entries(presets).find(([, v]) => v[0] === from && v[1] === to)?.[0] ?? 'custom';
  const wallets = useWallets();
  const valid = /^\d{4}-\d{2}-\d{2}$/.test(from) && /^\d{4}-\d{2}-\d{2}$/.test(to) && from <= to;
  const q = useQuery<Report>(valid ? `/reports/summary${qs({ from, to, walletId })}` : null);

  return (
    <div className="page">
      <PageHead title="Отчёты" />
      <div className="filters">
        <Segmented label="Период" value={preset} onChange={(v) => { const p = presets[v]; if (p) setQuery({ from: p[0], to: p[1] }); }} options={[
          ...(presets.period ? [{ value: 'period', label: 'Бюджетный период' }] : []),
          { value: 'month', label: 'Этот месяц' }, { value: 'prev', label: 'Прошлый' },
          { value: 'quarter', label: '3 месяца' }, { value: 'year', label: '12 месяцев' },
        ]} />
        <Input type="date" aria-label="С" value={from} onChange={(e) => setQuery({ from: e.target.value })} />
        <Input type="date" aria-label="По" value={to} onChange={(e) => setQuery({ to: e.target.value })} />
        <Select aria-label="Кошелёк" value={walletId} onChange={(e) => setQuery({ wallet: e.target.value })}>
          <option value="">Все кошельки</option>
          {(wallets.data ?? []).map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
        </Select>
      </div>

      {!valid ? <p role="alert" className="neg">Выберите даты: начало должно быть не позже конца.</p> : q.error && !q.data ? <ErrorBox error={q.error} onRetry={q.reload} /> : !q.data ? <ReportSkeleton /> : <ReportBody key={`${from}:${to}:${walletId}`} r={q.data} walletId={walletId} />}
      {valid ? <PaceReport from={from} to={to} walletId={walletId} /> : null}
    </div>
  );
}

type Row = Report['byCategory'][number];
function Bars({ list, kind, denom, from, to, nested, walletId, categories }: { list: Row[]; kind: 'exp' | 'inc'; denom: number; from: string; to: string; nested: boolean; walletId: string; categories: Category[] }) {
  const groups = nested ? groupCategories(list, categories) : [...list].sort((a, b) => Math.abs(b.total) - Math.abs(a.total)).map((row) => ({ row, total: row.total, count: row.count, children: [] as Row[] }));
  const max = Math.max(1, ...groups.map((g) => Math.abs(g.total)));
  const abs = (v: number) => Math.abs(v);
  const link = (id: number) => `/reports/category${qs({ category: id, from, to, wallet: walletId })}`;
  const bar = (row: Row, total: number, count: number, child: boolean, hasChildren: boolean) => (
    <Link to={link(row.categoryId)} className={cx('bar-row link-like', child && 'bar-child')}>
      <Tile icon={categoryIcon({ name: row.name, kind: row.kind })} color={categoryColor(row)} size="sm" />
      <span className="bar-label">{row.name}<span className="muted small"> {count}{hasChildren ? ' · с подкатегориями' : ''}</span></span>
      <span className="bar-val">{rub(abs(total))} <span className="muted small">{pct(abs(total) / (denom || 1))}</span></span>
      <span className="bar-track"><span className={cx('bar-fill', kind === 'exp' ? 'fill-exp' : 'fill-inc')} style={{ width: `${(abs(total) / max) * 100}%` }} /></span>
    </Link>
  );
  return (
    <ul className="bars">
      {groups.map((g) => (
        <li key={g.row.categoryId}>
          {bar(g.row, g.total, g.count, false, g.children.length > 0)}
          {g.children.length ? (
            <ul className="bars">
              {g.children.map((c) => <li key={c.categoryId}>{bar(c, c.total, c.count, true, false)}</li>)}
            </ul>
          ) : null}
        </li>
      ))}
      {!groups.length ? <li className="muted">{kind === 'exp' ? 'Расходов нет.' : 'Доходов нет.'}</li> : null}
    </ul>
  );
}

/** Money Lover's category pie as a ring: the biggest parents, the rest folded into «Другое» */
function Donut({ list, total, label, categories }: { list: Row[]; total: number; label: string; categories: Category[] }) {
  const groups = groupCategories(list, categories);
  const top = groups.slice(0, 6).map((g) => ({ name: g.row.name, value: Math.abs(g.total), color: categoryColor(g.row) }));
  const rest = groups.slice(6).reduce((s, g) => s + Math.abs(g.total), 0);
  const slices = rest > 0 ? [...top, { name: 'Другое', value: rest, color: 'var(--muted)' }] : top;
  if (!slices.length) return null;
  const arcs = pie<{ name: string; value: number; color: string }>().value((d) => d.value).sort(null).padAngle(0.012)(slices);
  const shape = arc<PieArcDatum<{ name: string; value: number; color: string }>>().innerRadius(62).outerRadius(88).cornerRadius(3);
  return (
    <figure className="donut">
      <svg viewBox="-90 -90 180 180" role="img" aria-label={`${label}: ${slices.map((s) => `${s.name} ${pct(s.value / (total || 1))}`).join(', ')}`}>
        {arcs.map((a) => <path key={a.data.name} d={shape(a) ?? ''} fill={a.data.color}><title>{`${a.data.name}: ${rub(a.data.value)}`}</title></path>)}
        <text className="donut-total" textAnchor="middle" dominantBaseline="middle">{rub(total)}</text>
      </svg>
      <figcaption className="donut-legend">
        {slices.map((s) => <span key={s.name}><i style={{ background: s.color }} />{s.name}<span className="muted"> {pct(s.value / (total || 1))}</span></span>)}
      </figcaption>
    </figure>
  );
}

export function ReportBody({ r, walletId = '' }: { r: Report; walletId?: string }) {
  const categories = useCategories();
  // «подкатегории внутри родителя» on by default, like Money Lover's report setting
  const [nested, setNested] = useState(() => { try { return localStorage.getItem('fin.reportFlat') !== '1'; } catch { return true; } });
  const toggle = (v: boolean) => { setNested(v); try { localStorage.setItem('fin.reportFlat', v ? '0' : '1'); } catch { /* storage unavailable */ } };
  return <ReportContent r={r} walletId={walletId} categories={categories.data ?? []} nested={nested} onNestedChange={toggle} />;
}

/** Pure presentation, also used for fixture rendering without an application build. */
export function ReportContent({ r, walletId = '', categories = [], nested = true, onNestedChange = () => {} }: { r: Report; walletId?: string; categories?: Category[]; nested?: boolean; onNestedChange?: (value: boolean) => void }) {
  const expenses = r.byCategory.filter(c => c.total < 0), incomes = r.byCategory.filter(c => c.total > 0);
  const net = r.income - r.expense, scale = Math.max(r.income, r.expense, 1);
  return (
    <>
      <section className="section report-top" aria-label="Остатки и итог">
        <dl className="report-balances">
          <div><dt>Начальный остаток</dt><dd className="money">{rub(r.openingBalance)}</dd></div>
          <Icon name="arrow" size={18} />
          <div><dt>Конечный остаток</dt><dd className="money">{rub(r.closingBalance)}</dd></div>
        </dl>
        <div className="report-net">
          <span className="muted small">Чистый итог</span>
          <strong className={cx('money', net < 0 ? 'neg' : 'pos')}>{rub(net, { sign: true })}</strong>
          <div className="net-bars">
            <span className="net-row"><span className="net-label">Доходы</span><span className="net-track"><i className="fill-inc" style={{ width: `${(r.income / scale) * 100}%` }} /></span><span className="money pos">{rub(r.income)}</span></span>
            <span className="net-row"><span className="net-label">Расходы</span><span className="net-track"><i className="fill-exp" style={{ width: `${(r.expense / scale) * 100}%` }} /></span><span className="money neg">{rub(r.expense)}</span></span>
          </div>
        </div>
      </section>
      <TopExpenses r={r} walletId={walletId} categories={categories} />
      {r.byMonth.length > 1 ? <Section title="По месяцам"><MonthChart data={r.byMonth} /></Section> : null}
      <label className="check report-nested"><input type="checkbox" checked={nested} onChange={(e) => onNestedChange(e.target.checked)} /> Подкатегории внутри родительских</label>
      <div className="grid-2">
        <Section title="Расходы по категориям">
          <Donut list={expenses} total={r.expense} label="Расходы по категориям" categories={categories} />
          <Bars list={expenses} kind="exp" denom={r.expense} from={r.from} to={r.to} nested={nested} walletId={walletId} categories={categories} />
        </Section>
        <Section title="Доходы по категориям">
          <Donut list={incomes} total={r.income} label="Доходы по категориям" categories={categories} />
          <Bars list={incomes} kind="inc" denom={r.income} from={r.from} to={r.to} nested={nested} walletId={walletId} categories={categories} />
        </Section>
      </div>
      <DebtFlows report={r} walletId={walletId} />
    </>
  );
}

/** the five biggest spending categories of the range (moved here from the period screen, owner 2026-10-30) */
function TopExpenses({ r, walletId, categories }: { r: Report; walletId: string; categories: Category[] }) {
  const top = groupCategories(r.byCategory.filter(c => c.total < 0), categories).slice(0, 5);
  return <Section title="Наибольшие расходы">
    {top.length ? <ul className="report-flow-list">{top.map(g => <li key={g.row.categoryId}><Link to={`/reports/category${qs({ category: g.row.categoryId, from: r.from, to: r.to, wallet: walletId })}`} className="report-flow-row link-like"><span>{g.row.name}</span><strong className="money">{rub(Math.abs(g.total))}</strong><Icon name="right" size={16} /></Link></li>)}</ul> : <p className="muted">Расходов нет.</p>}
  </Section>;
}

function MonthChart({ data }: { data: Report['byMonth'] }) {
  const W = 760, H = 220, m = { t: 12, r: 8, b: 26, l: 56 };
  const x = scaleBand().domain(data.map((d) => d.month)).range([m.l, W - m.r]).padding(0.25);
  const y = scaleLinear().domain([0, Math.max(1, ...data.map((d) => Math.max(d.income, d.expense)))]).nice().range([H - m.b, m.t]);
  const bw = x.bandwidth() / 2;
  const label = (mm: string) => { const [yy, mo] = mm.split('-').map(Number); return `${monthShort(mo - 1)}${data.length > 12 || mo === 1 ? ` ${String(yy).slice(2)}` : ''}`; };
  return (
    <figure className="chart">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Доходы и расходы по месяцам">
        {y.ticks(4).map((t) => (
          <g key={t}><line x1={m.l} x2={W - m.r} y1={y(t)} y2={y(t)} className="grid" />
            <text x={m.l - 8} y={y(t)} className="axis" textAnchor="end" dominantBaseline="middle">{t >= 1000 ? `${Math.round(t / 1000)}к` : t}</text></g>
        ))}
        {data.map((d) => (
          <g key={d.month}>
            <rect x={x(d.month)!} y={y(d.income)} width={bw - 1} height={H - m.b - y(d.income)} className="bar-inc" rx={2}><title>{`${d.month}: доходы ${rub(d.income)}`}</title></rect>
            <rect x={x(d.month)! + bw} y={y(d.expense)} width={bw - 1} height={H - m.b - y(d.expense)} className="bar-exp" rx={2}><title>{`${d.month}: расходы ${rub(d.expense)}`}</title></rect>
            <text x={x(d.month)! + x.bandwidth() / 2} y={H - 8} className="axis" textAnchor="middle">{label(d.month)}</text>
          </g>
        ))}
      </svg>
      <div className="chart-legend"><span><i className="lg-inc" />Доходы</span><span><i className="lg-exp" />Расходы</span></div>
    </figure>
  );
}

function DebtFlows({ report, walletId }: { report: Report; walletId: string }) {
  const flows = report.debtFlows;
  if (!flows) return null;
  const rows = [
    ['debt', 'Взял в долг', flows.borrowed],
    ['loan', 'Дал в долг', flows.lent],
    ['repayment', 'Вернул долг', flows.repaid],
    ['debt_collection', 'Мне вернули', flows.collected],
  ] as const;
  return <Section title="Долги и займы"><ul className="report-flow-list">{rows.map(([kind, label, value]) => <li key={kind}><Link to={`/transactions${qs({ from: report.from, to: report.to, wallet: walletId, kind, included: 1 })}`} className="report-flow-row link-like"><span>{label}</span><strong className="money">{rub(value)}</strong><Icon name="right" size={16} /></Link></li>)}</ul></Section>;
}

function PaceReport({ from, to, walletId }: { from: string; to: string; walletId: string }) {
  const [shown, setShown] = useState(true);
  const today = todayIso(), cutoff = to > today ? today : to;
  const enabled = from <= cutoff && daysBetween(from, to) <= 31;
  const q = useQuery<ReportPace>(enabled ? `/reports/pace${qs({ from, to: cutoff, walletId })}` : null);
  if (!enabled) return null;
  return <Section title="Темп расходов" aside={<button type="button" className="link-like" aria-expanded={shown} onClick={() => setShown(v => !v)}>{shown ? 'Скрыть' : 'Показать'}</button>}>
    {shown ? q.error && !q.data ? <ErrorBox error={q.error} onRetry={q.reload} /> : !q.data ? <ReportSkeleton /> : <>
      <p className="muted small">Нарастающим итогом по {dateShort(cutoff)} · среднее за тот же отрезок трёх прошлых месяцев</p>
      <TrendChart labels={q.data.days.map(d => d.date)} currency={q.data.currency} label="Расходы и среднее за три месяца" series={[
        { name: 'Расходы', tone: 'expense', values: q.data.days.map((d, x) => ({ x, value: d.total })) },
        ...(q.data.baseline ? [{ name: 'Среднее за 3 месяца', tone: 'baseline' as const, dashed: true, values: q.data.baseline.map((value, x) => ({ x, value })) }] : []),
      ]} />
      {!q.data.baseline ? <p className="muted small">В предыдущих трёх отрезках расходов нет — сравнение пока недоступно.</p> : null}
    </> : null}
  </Section>;
}
